import { WaveFile } from 'wavefile';
import { datadogRum } from '@datadog/browser-rum';
import { createDevLogger } from '../utils/devLog';

const logger = createDevLogger('recordingUtils');

export const encodeWav = async blob => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result.replace('data:audio/wav;base64,', '');
            resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

// Converts recorded audio to backend format (16kHz mono WAV)
// Records at native sample rate, then converts to avoid device-specific issues
export const convertForAnalysisService = async originalBlob => {
    const arrayBuffer = await originalBlob.arrayBuffer();
    const wav = new WaveFile();
    wav.fromBuffer(new Uint8Array(arrayBuffer));

    const targetSampleRate = 16000;

    // Convert stereo to mono if needed
    if (wav.fmt.numChannels > 1) {
        wav.toMono();
    }

    // Resample to target rate if needed
    if (wav.fmt.sampleRate !== targetSampleRate) {
        wav.toSampleRate(targetSampleRate);
    }

    const convertedBuffer = wav.toBuffer();
    return new Blob([convertedBuffer], { type: 'audio/wav' });
};

export const analyzeAudioForSilence = async (recordedResult, customThreshold) => {
    if (!recordedResult) {
        return { isSilent: true, rms: 0, duration: 0, error: 'No audio data' };
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    try {
        const audioBuffer = await recordedResult.arrayBuffer();

        if (audioBuffer.byteLength === 0) {
            return { isSilent: true, rms: 0, duration: 0, error: 'Empty buffer' };
        }

        const decodedAudio = await audioContext.decodeAudioData(audioBuffer);
        const { duration, sampleRate, numberOfChannels } = decodedAudio;

        // Validate decoded audio properties
        if (!duration || duration <= 0) {
            return { isSilent: true, rms: 0, duration: 0, error: 'Invalid duration' };
        }

        const channelData = decodedAudio.getChannelData(0);

        if (!channelData || channelData.length === 0) {
            return { isSilent: true, rms: 0, duration, error: 'No channel data' };
        }

        // Calculate RMS and peak amplitude
        let sum = 0;
        let maxAmplitude = 0;

        for (let i = 0; i < channelData.length; i += 1) {
            const sample = channelData[i];
            sum += sample * sample;
            maxAmplitude = Math.max(maxAmplitude, Math.abs(sample));
        }

        const rms = Math.sqrt(sum / channelData.length);
        const threshold = customThreshold || 0.01;
        const isSilent = rms < threshold && maxAmplitude < threshold * 3;

        logger.log(
            `Audio: ${duration.toFixed(2)}s, ${sampleRate}Hz, ${numberOfChannels}ch, ` +
                `RMS: ${rms.toFixed(4)}, Peak: ${maxAmplitude.toFixed(4)}, Silent: ${isSilent}`,
        );

        return {
            isSilent,
            rms,
            maxAmplitude,
            duration,
            sampleRate,
            numberOfChannels,
            error: null,
        };
    } catch (error) {
        logger.error('Audio analysis error:', error);
        return {
            isSilent: true,
            rms: 0,
            maxAmplitude: 0,
            duration: 0,
            error: error.message || 'Decode failed',
        };
    } finally {
        await audioContext.close().catch(() => {});
    }
};

// Silence detection configuration
const SILENCE_CONFIG = {
    rmsThreshold: 0.01,
    minDuration: 0.5, // seconds
    minBlobSize: 1000, // bytes
};

/**
 * Checks if recorded audio is silent/empty before sending to backend.
 * Returns FAILED result to trigger retry, or null to proceed to backend.
 */
export const checkForSilentAudio = async (recordedResult, verificationType) => {
    // Validate blob exists and has reasonable size
    if (!recordedResult || !(recordedResult instanceof Blob)) {
        const reason = 'no_blob';
        logger.error(`Silent audio detected: ${reason}. not sending to backend.`);
        datadogRum.addAction('silence_check_failed', {
            verificationType,
            client_validation_failed: reason,
        });
        return {
            verification_passed: false,
            client_validation_failed: reason,
        };
    }

    if (recordedResult.size === 0 || recordedResult.size < SILENCE_CONFIG.minBlobSize) {
        const reason = recordedResult.size === 0 ? 'empty_blob' : 'tiny_blob';
        logger.error(`Silent audio detected: ${reason}. not sending to backend.`);
        datadogRum.addAction('silence_check_failed', {
            verificationType,
            client_validation_failed: reason,
            size: recordedResult.size,
        });
        return {
            verification_passed: false,
            client_validation_failed: reason,
        };
    }

    // Analyze audio content
    try {
        const analysis = await analyzeAudioForSilence(recordedResult);

        if (analysis.error) {
            const reason = 'analysis_error';
            logger.error(`Silent audio detected: ${reason}. not sending to backend.`);
            datadogRum.addAction('silence_check_failed', {
                verificationType,
                client_validation_failed: reason,
                error: analysis.error,
            });
            return {
                verification_passed: false,
                client_validation_failed: reason,
            };
        }

        const isTooQuiet = analysis.rms < SILENCE_CONFIG.rmsThreshold;
        const isTooShort =
            typeof analysis.duration === 'number' && analysis.duration < SILENCE_CONFIG.minDuration;

        if (analysis.isSilent || isTooQuiet || isTooShort) {
            const reason = 'silent_audio';
            const specificReason = analysis.isSilent
                ? 'silent'
                : isTooQuiet
                ? 'too_quiet'
                : 'too_short';
            logger.error(`Silent audio detected: ${reason}. not sending to backend.`);
            datadogRum.addAction('silence_check_failed', {
                verificationType,
                client_validation_failed: reason,
                specific_reason: specificReason,
                rms: analysis.rms,
                duration: analysis.duration,
            });
            return {
                verification_passed: false,
                client_validation_failed: reason,
            };
        }

        datadogRum.addAction('silence_check_passed', {
            verificationType,
            rms: analysis.rms,
            duration: analysis.duration,
        });
        return null;
    } catch (error) {
        // Unexpected error in our code - fail-open to backend
        datadogRum.addError(error, {
            context: 'silence_check_unexpected_error',
            verificationType,
        });
        return null;
    }
};
